import { useCallback, useMemo } from "react"
import { type Query as CubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  useCustomAttributionControllerGetAllMediaTargetsQuery,
  useCustomAttributionControllerGetAllModelsQuery,
  type CustomAttributionModelWithWeights,
} from "~/ui-rtk/api/customAttributionApi"
import { AdChannel } from "~/ui-rtk/api/types/custom-attribution-enums"
import {
  calculateModeledRevenueForRow,
  calculateBrandValueBreakdownForRow,
} from "~/ui-rtk/utils/calculateModeledRevenue"
import type { AdsetRow, Momentum } from "../types"

const CUBE_ADSETS = "company_media_metrics_adsets"
const CUBE_ADS = "company_media_metrics_ads"
const LAST_UPDATED = "company_last_updated_date"

/** Explicit limit — Cube REST defaults to 10k rows */
const CUBE_ROW_LIMIT = 50000

// Momentum thresholds. All deltas are percent change vs the prior 7-day
// window; a signal with a null delta (no meaningful prior base) never fires.
/** ROAS drop that fires a fatigue signal */
export const MOMENTUM_ROAS_DROP_PCT = -15
/** CPM rise that fires a fatigue signal */
export const MOMENTUM_CPM_RISE_PCT = 20
/** Engagement-rate drop that fires a fatigue signal */
export const MOMENTUM_ENGAGEMENT_DROP_PCT = -15
/** Impressions drop that fires a fatigue signal */
export const MOMENTUM_IMPRESSIONS_DROP_PCT = -20
/** Fatiguing requires at least this many fired signals... */
export const MOMENTUM_FATIGUE_MIN_SIGNALS = 2
/** ...while spend held roughly steady (a spend crash explains the drops) */
export const MOMENTUM_FATIGUE_SPEND_FLOOR_PCT = -5
/** Scaling requires spend up at least this much... */
export const MOMENTUM_SCALING_SPEND_RISE_PCT = 20
/** ...without ROAS degrading beyond this floor */
export const MOMENTUM_SCALING_ROAS_FLOOR_PCT = -5

// Revenue-related measures needed for frontend modeled_revenue calculation.
// Short names (no cube prefix) — these are the keys of the per-window measure
// records and what calculateModeledRevenueForRow expects. Kept in sync with
// REVENUE_MEASURE_NAMES in
// pages/BudgetRecommender/hooks/useBudgetRecommendations.ts so Modeled ROAS /
// BROAS here match the BudgetRecommender figures for the same window.
const REVENUE_MEASURE_NAMES = [
  "spend",
  "modeled_revenue",
  "brand_value_agg",
  "budget_agg",
  "purchases",
  "ga4_total_revenue",
  "purchase_value_1d_view",
  "purchase_value_1d_engaged_view",
  "revenue_from_search",
  "d30_brand_value",
  "d60_brand_value",
  "d90_brand_value",
  // Shopify
  "shopify_first_touch_revenue",
  "shopify_last_touch_revenue",
  // Meta incrementality
  "purchase_value_incrementality",
  // Triple Whale (standard)
  "triple_whale_linear_total_revenue",
  "triple_whale_last_platform_click_total_revenue",
  "triple_whale_full_first_click_total_revenue",
  "triple_whale_full_last_click_total_revenue",
  "triple_whale_linear_all_total_revenue",
  // Triple Whale (new customer)
  "triple_whale_linear_nc_total_revenue",
  "triple_whale_last_platform_click_nc_total_revenue",
  "triple_whale_full_first_click_nc_total_revenue",
  "triple_whale_full_last_click_nc_total_revenue",
  "triple_whale_linear_all_nc_total_revenue",
]

const ENGAGEMENT_MEASURE_NAMES = ["likes", "comments", "shares", "saves"]

const ADSET_MEASURE_NAMES = [
  ...REVENUE_MEASURE_NAMES,
  "impressions",
  ...ENGAGEMENT_MEASURE_NAMES,
  "days_with_spend",
]

interface MediaTarget {
  id: string
  initiativeType: string
  type: string
  ad_set_id: string
  campaign_id: string
}

/** One ad set's aggregate over a single 7-day window. */
export interface AdsetWindowRow {
  adsetId: string
  adsetName: string
  campaignId: string
  campaignName: string
  status: string
  objective: string | null
  optimizationGoal: string | null
  /** Short measure name → value aggregated over the window */
  measures: Record<string, number>
}

/** One ad's aggregate over a single 7-day window. */
export interface AdWindowRow {
  adsetId: string
  adId: string
  status: string
  spend: number
}

/** Add delta days (may be negative) to a YYYY-MM-DD date */
export function addDays(date: string, delta: number): string {
  const [y, m, d] = date.split("-").map(Number)
  // Date.UTC avoids local-timezone shift when serializing via toISOString
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().split("T")[0]
}

export interface AnalysisWindows {
  current7: [string, string]
  prior7: [string, string]
}

/** Windows anchored to the last complete data day: dataThrough = n − 1 where
 *  n is the newest day with any data (the newest day is likely partial). */
export function deriveWindows(maxDataDate: string): {
  dataThrough: string
  windows: AnalysisWindows
} {
  const dataThrough = addDays(maxDataDate, -1)
  return {
    dataThrough,
    windows: {
      current7: [addDays(dataThrough, -6), dataThrough],
      prior7: [addDays(dataThrough, -13), addDays(dataThrough, -7)],
    },
  }
}

/** % change of current vs prior; null when the prior window has no
 *  meaningful base (0 or negative). */
export function computeDeltaPct(current: number, prior: number): number | null {
  if (!(prior > 0)) return null
  return ((current - prior) / prior) * 100
}

/** Share of adset spend concentrated in its top-5 ads by spend; null when
 *  the adset spent nothing in the window. */
export function computeTop5Share(
  adSpends: number[],
  adsetSpend: number,
): number | null {
  if (!(adsetSpend > 0)) return null
  const top5Sum = [...adSpends]
    .sort((a, b) => b - a)
    .slice(0, 5)
    .reduce((s, v) => s + v, 0)
  return top5Sum / adsetSpend
}

export interface MomentumInputs {
  spendDeltaPct: number | null
  roasDeltaPct: number | null
  cpmDeltaPct: number | null
  engagementRateDeltaPct: number | null
  impressionsDeltaPct: number | null
}

function formatSignal(name: string, deltaPct: number): string {
  return `${name} ${deltaPct > 0 ? "+" : ""}${deltaPct.toFixed(0)}%`
}

/**
 * Fatiguing = ≥2 fired signals while spend held roughly steady (a spend crash
 * explains the drops on its own). Scaling = spend up sharply without ROAS
 * degrading. Otherwise Stable. Checked in that order.
 */
export function deriveMomentum(inputs: MomentumInputs): {
  momentum: Momentum
  signals: string[]
} {
  const signals: string[] = []
  if (
    inputs.roasDeltaPct !== null &&
    inputs.roasDeltaPct <= MOMENTUM_ROAS_DROP_PCT
  ) {
    signals.push(formatSignal("ROAS", inputs.roasDeltaPct))
  }
  if (
    inputs.cpmDeltaPct !== null &&
    inputs.cpmDeltaPct >= MOMENTUM_CPM_RISE_PCT
  ) {
    signals.push(formatSignal("CPM", inputs.cpmDeltaPct))
  }
  if (
    inputs.engagementRateDeltaPct !== null &&
    inputs.engagementRateDeltaPct <= MOMENTUM_ENGAGEMENT_DROP_PCT
  ) {
    signals.push(formatSignal("Engagement rate", inputs.engagementRateDeltaPct))
  }
  if (
    inputs.impressionsDeltaPct !== null &&
    inputs.impressionsDeltaPct <= MOMENTUM_IMPRESSIONS_DROP_PCT
  ) {
    signals.push(formatSignal("Impressions", inputs.impressionsDeltaPct))
  }

  if (
    signals.length >= MOMENTUM_FATIGUE_MIN_SIGNALS &&
    inputs.spendDeltaPct !== null &&
    inputs.spendDeltaPct >= MOMENTUM_FATIGUE_SPEND_FLOOR_PCT
  ) {
    return { momentum: "FATIGUING", signals }
  }
  if (
    inputs.spendDeltaPct !== null &&
    inputs.spendDeltaPct >= MOMENTUM_SCALING_SPEND_RISE_PCT &&
    inputs.roasDeltaPct !== null &&
    inputs.roasDeltaPct >= MOMENTUM_SCALING_ROAS_FLOOR_PCT
  ) {
    return { momentum: "SCALING", signals }
  }
  return { momentum: "STABLE", signals }
}

function sumEngagements(measures: Record<string, number>): number {
  return ENGAGEMENT_MEASURE_NAMES.reduce((s, m) => s + (measures[m] || 0), 0)
}

export interface BuildAdsetRowsArgs {
  currentAdsets: AdsetWindowRow[]
  priorAdsets: AdsetWindowRow[]
  currentAds: AdWindowRow[]
  priorAds: AdWindowRow[]
  /** Modeled revenue + brand value for one window's aggregated measures
   *  (attribution-model weighted, BudgetRecommender parity) */
  resolveModeled: (adset: AdsetWindowRow) => {
    modeledRevenue: number
    brandValue: number
  }
}

/**
 * Derive the page's ad set rows from the two window aggregates. Hides
 * $0-forever rows: an ad set surfaces only with spend in either window or
 * ACTIVE status. Ad sets present only in the prior window still surface (with
 * a zero current window) so a just-paused ad set doesn't silently vanish.
 */
export function buildAdsetRows({
  currentAdsets,
  priorAdsets,
  currentAds,
  priorAds,
  resolveModeled,
}: BuildAdsetRowsArgs): AdsetRow[] {
  const priorByAdset = new Map(priorAdsets.map(a => [a.adsetId, a]))

  const groupAds = (ads: AdWindowRow[]) => {
    const byAdset = new Map<string, AdWindowRow[]>()
    for (const ad of ads) {
      let list = byAdset.get(ad.adsetId)
      if (!list) {
        list = []
        byAdset.set(ad.adsetId, list)
      }
      list.push(ad)
    }
    return byAdset
  }
  const currentAdsByAdset = groupAds(currentAds)
  const priorAdsByAdset = groupAds(priorAds)

  const countActive = (ads: AdWindowRow[]) =>
    ads.filter(a => a.status === "ACTIVE").length

  const rows: AdsetRow[] = []

  const process = (
    adset: AdsetWindowRow,
    prior: AdsetWindowRow | undefined,
  ) => {
    const spend7 = adset.measures.spend || 0
    const spendPrior7 = prior?.measures.spend || 0
    if (spend7 <= 0 && spendPrior7 <= 0 && adset.status !== "ACTIVE") return

    const windowAds = currentAdsByAdset.get(adset.adsetId) ?? []
    const priorWindowAds = priorAdsByAdset.get(adset.adsetId) ?? []

    const activeAds = countActive(windowAds)
    const priorActiveAds = countActive(priorWindowAds)

    const avgDailySpendPerActiveAd =
      activeAds > 0 ? spend7 / 7 / activeAds : null
    const priorAvgDaily =
      priorActiveAds > 0 ? spendPrior7 / 7 / priorActiveAds : null

    const top5Share = computeTop5Share(
      windowAds.map(a => a.spend),
      spend7,
    )
    const priorTop5Share = computeTop5Share(
      priorWindowAds.map(a => a.spend),
      spendPrior7,
    )

    const { modeledRevenue, brandValue } = resolveModeled(adset)
    const priorModeled = prior
      ? resolveModeled(prior)
      : { modeledRevenue: 0, brandValue: 0 }

    const modeledRoas = spend7 > 0 ? modeledRevenue / spend7 : null
    const priorRoas =
      spendPrior7 > 0 ? priorModeled.modeledRevenue / spendPrior7 : null
    const modeledBroas = spend7 > 0 ? brandValue / spend7 : null
    const priorBroas =
      spendPrior7 > 0 ? priorModeled.brandValue / spendPrior7 : null

    const impressions = adset.measures.impressions || 0
    const priorImpressions = prior?.measures.impressions || 0
    const cpm = impressions > 0 ? (spend7 / impressions) * 1000 : null
    const priorCpm =
      priorImpressions > 0 ? (spendPrior7 / priorImpressions) * 1000 : null
    const engagementRate =
      impressions > 0 ? sumEngagements(adset.measures) / impressions : null
    const priorEngagementRate =
      prior && priorImpressions > 0
        ? sumEngagements(prior.measures) / priorImpressions
        : null

    const spendDeltaPct = computeDeltaPct(spend7, spendPrior7)
    const modeledRoasDeltaPct =
      modeledRoas !== null && priorRoas !== null
        ? computeDeltaPct(modeledRoas, priorRoas)
        : null

    const { momentum, signals } = deriveMomentum({
      spendDeltaPct,
      roasDeltaPct: modeledRoasDeltaPct,
      cpmDeltaPct:
        cpm !== null && priorCpm !== null
          ? computeDeltaPct(cpm, priorCpm)
          : null,
      engagementRateDeltaPct:
        engagementRate !== null && priorEngagementRate !== null
          ? computeDeltaPct(engagementRate, priorEngagementRate)
          : null,
      impressionsDeltaPct: computeDeltaPct(impressions, priorImpressions),
    })

    rows.push({
      adsetId: adset.adsetId,
      adsetName: adset.adsetName,
      campaignId: adset.campaignId,
      campaignName: adset.campaignName,
      status: adset.status,
      spend7,
      spendDeltaPct,
      activeAds,
      activeAdsDelta: activeAds - priorActiveAds,
      avgDailySpendPerActiveAd,
      avgDailySpendDeltaPct:
        avgDailySpendPerActiveAd !== null && priorAvgDaily !== null
          ? computeDeltaPct(avgDailySpendPerActiveAd, priorAvgDaily)
          : null,
      top5Share,
      top5ShareDeltaPp:
        top5Share !== null && priorTop5Share !== null
          ? (top5Share - priorTop5Share) * 100
          : null,
      modeledRoas,
      modeledRoasDeltaPct,
      modeledBroas,
      modeledBroasDeltaPct:
        modeledBroas !== null && priorBroas !== null
          ? computeDeltaPct(modeledBroas, priorBroas)
          : null,
      momentum,
      momentumSignals: signals,
      modeledRevenue7: modeledRevenue,
      brandValue7: brandValue,
    })
  }

  const seen = new Set<string>()
  for (const adset of currentAdsets) {
    seen.add(adset.adsetId)
    process(adset, priorByAdset.get(adset.adsetId))
  }
  for (const prior of priorAdsets) {
    if (seen.has(prior.adsetId)) continue
    process({ ...prior, measures: {} }, prior)
  }

  return rows
}

/** Find the best matching attribution model using 3-tier matching (mirrors
 *  the BudgetRecommender):
 *  1. 4-field (initiativeType + adChannel + objective + optimizationGoal)
 *  2. 2-field (initiativeType + adChannel)
 *  3. 1-field (initiativeType only)
 */
function findModel(
  models: CustomAttributionModelWithWeights[],
  initiativeType: string,
  adChannel: AdChannel,
  objective?: string | null,
  optimizationGoal?: string | null,
): CustomAttributionModelWithWeights | undefined {
  if (objective || optimizationGoal) {
    const exact4 = models.find(
      m =>
        String(m.initiativeType) === String(initiativeType) &&
        m.adChannel === adChannel &&
        (!objective || String(m.adChannelObjective) === String(objective)) &&
        (!optimizationGoal ||
          String(m.adChannelOptimizationGoal) === String(optimizationGoal)),
    )
    if (exact4) return exact4
  }
  const exact2 = models.find(
    m =>
      String(m.initiativeType) === String(initiativeType) &&
      m.adChannel === adChannel,
  )
  if (exact2) return exact2
  return models.find(m => String(m.initiativeType) === String(initiativeType))
}

/** For compareDateRange queries, access the underlying loadResponses —
 *  responses arrive in the order the ranges were declared ([prior, current]).
 *  Mirrors the parsing in pages/Revenue/KPI/connect.ts. */
function windowData(
  resultSet: unknown,
  index: number,
): Array<Record<string, unknown>> {
  const withResponses = resultSet as {
    loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
  }
  return withResponses.loadResponses?.[index]?.data ?? []
}

/** Collapse one window's raw adset rows into one entry per ad set. The
 *  adset_status dimension can split an ad set across rows when its status
 *  changed mid-window: measures sum across the split (budget_agg via max,
 *  matching its max-over-days semantics) and status reads ACTIVE if any row
 *  says so. */
function parseAdsetWindow(
  raw: Array<Record<string, unknown>>,
): AdsetWindowRow[] {
  const byId = new Map<string, AdsetWindowRow>()
  for (const row of raw) {
    const adsetId = String(row[`${CUBE_ADSETS}.adset_id`] ?? "")
    if (!adsetId) continue
    let entry = byId.get(adsetId)
    if (!entry) {
      entry = {
        adsetId,
        adsetName: String(row[`${CUBE_ADSETS}.adset_name`] ?? "Unknown"),
        campaignId: String(row[`${CUBE_ADSETS}.campaign_id`] ?? ""),
        campaignName: String(row[`${CUBE_ADSETS}.campaign_name`] ?? ""),
        status: String(row[`${CUBE_ADSETS}.adset_status`] ?? ""),
        objective: String(row[`${CUBE_ADSETS}.objective`] ?? "") || null,
        optimizationGoal:
          String(row[`${CUBE_ADSETS}.optimization_goal`] ?? "") || null,
        measures: {},
      }
      byId.set(adsetId, entry)
    }
    if (String(row[`${CUBE_ADSETS}.adset_status`] ?? "") === "ACTIVE") {
      entry.status = "ACTIVE"
    }
    for (const m of ADSET_MEASURE_NAMES) {
      const value = Number(row[`${CUBE_ADSETS}.${m}`]) || 0
      entry.measures[m] =
        m === "budget_agg"
          ? Math.max(entry.measures[m] ?? 0, value)
          : (entry.measures[m] ?? 0) + value
    }
  }
  return [...byId.values()]
}

/** Collapse one window's raw ad rows into one entry per ad (the ad_status
 *  dimension can split an ad the same way adset_status splits an ad set). */
function parseAdWindow(raw: Array<Record<string, unknown>>): AdWindowRow[] {
  const byId = new Map<string, AdWindowRow>()
  for (const row of raw) {
    const adId = String(row[`${CUBE_ADS}.ad_id`] ?? "")
    if (!adId) continue
    let entry = byId.get(adId)
    if (!entry) {
      entry = {
        adId,
        adsetId: String(row[`${CUBE_ADS}.adset_id`] ?? ""),
        status: String(row[`${CUBE_ADS}.ad_status`] ?? ""),
        spend: 0,
      }
      byId.set(adId, entry)
    }
    if (String(row[`${CUBE_ADS}.ad_status`] ?? "") === "ACTIVE") {
      entry.status = "ACTIVE"
    }
    entry.spend += Number(row[`${CUBE_ADS}.spend`]) || 0
  }
  return [...byId.values()]
}

export function useCreativeMetrics() {
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id ?? ""

  const { data: mediaTargets, isLoading: isLoadingTargets } =
    useCustomAttributionControllerGetAllMediaTargetsQuery()

  const {
    data: attributionModels = [] as CustomAttributionModelWithWeights[],
    isLoading: isLoadingModels,
  } = useCustomAttributionControllerGetAllModelsQuery()

  // Anchor windows to n − 1 where n is the newest day with both ad- and
  // adset-grain data (min of the two max dates; the newest day is partial)
  const maxDateQuery: CubeQuery = useMemo(
    () => ({
      dimensions: [
        `${LAST_UPDATED}.brand_media_paid_optimization_ads_max_date`,
        `${LAST_UPDATED}.brand_media_paid_optimization_adsets_max_date`,
      ],
      limit: 1,
    }),
    [],
  )

  const { resultSet: maxDateResult, isLoading: isLoadingMaxDate } =
    useCubeQuery(maxDateQuery, { skip: !companyId })

  const { dataThrough, windows } = useMemo((): {
    dataThrough: string | null
    windows: AnalysisWindows | null
  } => {
    if (!maxDateResult) return { dataThrough: null, windows: null }
    const rows = maxDateResult.tablePivot()
    if (!rows.length) return { dataThrough: null, windows: null }

    const row = rows[0]
    const adsMax = String(
      row[`${LAST_UPDATED}.brand_media_paid_optimization_ads_max_date`] ?? "",
    )
    const adsetsMax = String(
      row[`${LAST_UPDATED}.brand_media_paid_optimization_adsets_max_date`] ??
        "",
    )

    // Normalize to YYYY-MM-DD (Cube may return full ISO datetime strings)
    const toDateOnly = (s: string) => s.split("T")[0]
    const dates = [adsMax, adsetsMax].filter(d => d.length > 0).map(toDateOnly)
    if (dates.length === 0) return { dataThrough: null, windows: null }

    dates.sort()
    return deriveWindows(dates[0])
  }, [maxDateResult])

  const adsetQuery: CubeQuery = useMemo(
    () => ({
      dimensions: [
        `${CUBE_ADSETS}.adset_id`,
        `${CUBE_ADSETS}.adset_name`,
        `${CUBE_ADSETS}.campaign_id`,
        `${CUBE_ADSETS}.campaign_name`,
        `${CUBE_ADSETS}.adset_status`,
        `${CUBE_ADSETS}.objective`,
        `${CUBE_ADSETS}.optimization_goal`,
      ],
      measures: ADSET_MEASURE_NAMES.map(m => `${CUBE_ADSETS}.${m}`),
      filters: [
        {
          member: `${CUBE_ADSETS}.companyid`,
          operator: "equals" as const,
          values: [companyId],
        },
        {
          member: `${CUBE_ADSETS}.media_channel`,
          operator: "equals" as const,
          values: ["meta"],
        },
      ],
      timeDimensions: [
        {
          dimension: `${CUBE_ADSETS}.md_date`,
          compareDateRange: windows ? [windows.prior7, windows.current7] : [],
        },
      ],
      limit: CUBE_ROW_LIMIT,
    }),
    [companyId, windows],
  )

  const adsQuery: CubeQuery = useMemo(
    () => ({
      dimensions: [
        `${CUBE_ADS}.adset_id`,
        `${CUBE_ADS}.ad_id`,
        `${CUBE_ADS}.ad_status`,
      ],
      measures: [`${CUBE_ADS}.spend`],
      filters: [
        {
          member: `${CUBE_ADS}.companyid`,
          operator: "equals" as const,
          values: [companyId],
        },
        {
          member: `${CUBE_ADS}.media_channel`,
          operator: "equals" as const,
          values: ["meta"],
        },
      ],
      timeDimensions: [
        {
          dimension: `${CUBE_ADS}.md_date`,
          compareDateRange: windows ? [windows.prior7, windows.current7] : [],
        },
      ],
      limit: CUBE_ROW_LIMIT,
    }),
    [companyId, windows],
  )

  const {
    resultSet: adsetResult,
    isLoading: isLoadingAdsets,
    refetch: refetchAdsets,
  } = useCubeQuery(adsetQuery, { skip: !companyId || !windows })

  const {
    resultSet: adsResult,
    isLoading: isLoadingAds,
    refetch: refetchAds,
  } = useCubeQuery(adsQuery, { skip: !companyId || !windows })

  const targetLookup = useMemo(() => {
    const map = new Map<string, string>()
    for (const t of (mediaTargets as MediaTarget[] | undefined) ?? []) {
      // Meta assigns initiative types at the ad set level
      if (t.ad_set_id) map.set(t.ad_set_id, t.initiativeType)
    }
    return map
  }, [mediaTargets])

  const modelsArray = attributionModels as CustomAttributionModelWithWeights[]

  const rows = useMemo((): AdsetRow[] => {
    if (!adsetResult || !adsResult || !mediaTargets) return []

    const priorAdsets = parseAdsetWindow(windowData(adsetResult, 0))
    const currentAdsets = parseAdsetWindow(windowData(adsetResult, 1))
    const priorAds = parseAdWindow(windowData(adsResult, 0))
    const currentAds = parseAdWindow(windowData(adsResult, 1))

    // BudgetRecommender-parity modeled values: attribution model matched on
    // initiativeType + META + objective/optimization_goal, frontend-weighted
    // revenue with fallback to the cube's modeled_revenue measure.
    const resolveModeled = (adset: AdsetWindowRow) => {
      const type = targetLookup.get(adset.adsetId)
      if (!type) {
        return {
          modeledRevenue: adset.measures.modeled_revenue || 0,
          brandValue: adset.measures.brand_value_agg || 0,
        }
      }
      const model = findModel(
        modelsArray,
        type,
        AdChannel.META,
        adset.objective,
        adset.optimizationGoal,
      )
      const frontendMR = calculateModeledRevenueForRow({
        rowData: adset.measures,
        model,
        adChannel: AdChannel.META,
      })
      const bvBreakdown = calculateBrandValueBreakdownForRow({
        rowData: adset.measures,
        model,
      })
      return {
        modeledRevenue:
          frontendMR > 0 ? frontendMR : adset.measures.modeled_revenue || 0,
        brandValue:
          bvBreakdown.total > 0
            ? bvBreakdown.total
            : adset.measures.brand_value_agg || 0,
      }
    }

    return buildAdsetRows({
      currentAdsets,
      priorAdsets,
      currentAds,
      priorAds,
      resolveModeled,
    })
  }, [adsetResult, adsResult, mediaTargets, targetLookup, modelsArray])

  const refetch = useCallback(() => {
    refetchAdsets()
    refetchAds()
  }, [refetchAdsets, refetchAds])

  const isLoading =
    isLoadingMaxDate ||
    isLoadingAdsets ||
    isLoadingAds ||
    isLoadingTargets ||
    isLoadingModels

  return { rows, isLoading, dataThrough, windows, refetch }
}
